// src/components/PlotPanel.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCompass,
  faHashtag,
  faHome,
  faRulerCombined,
  faFileAlt,
  faChevronLeft,
  faChevronRight,
  faArrowRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";

import PaymentHandler from "./PaymentHandler";

/* =========================================================
   ✅ Popup: Prev/Next + Desktop wheel zoom + Drag pan
            + MOBILE PINCH ZOOM
   ========================================================= */
function ZoomableGalleryPopup({ open, images, index, title, onClose, onPrev, onNext }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const viewerRef = useRef(null);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0, baseX: 0, baseY: 0 });

  const pinchRef = useRef({
    active: false,
    startDist: 0,
    startScale: 1,
    startPos: { x: 0, y: 0 },
    startMid: { x: 0, y: 0 },
  });

  const current = images?.[index] || null;
  const src = current?.src || "";
  const slideLabel = current?.label || `Item ${index + 1}`;
  const canNav = (images?.length || 0) > 1;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const resetView = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
    draggingRef.current = false;
    pinchRef.current = {
      active: false,
      startDist: 0,
      startScale: 1,
      startPos: { x: 0, y: 0 },
      startMid: { x: 0, y: 0 },
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    resetView();
  }, [open, src, resetView]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") {
        resetView();
        onPrev?.();
      }
      if (e.key === "ArrowRight") {
        resetView();
        onNext?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onPrev, onNext, resetView]);

  const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
  const mid = (t1, t2) => ({ x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 });

  useEffect(() => {
    if (!open) return;
    const el = viewerRef.current;
    if (!el) return;

    const onWheelNative = (e) => {
      e.preventDefault();
      const next = e.deltaY > 0 ? scale * 0.92 : scale * 1.08;
      const s = clamp(next, 1, 6);
      setScale(s);
      if (s === 1) setPos({ x: 0, y: 0 });
    };

    const onTouchStartNative = (e) => {
      if (e.touches?.length === 2) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];
        pinchRef.current = {
          active: true,
          startDist: dist(t1, t2) || 1,
          startScale: scale,
          startPos: { ...pos },
          startMid: mid(t1, t2),
        };
        return;
      }

      if (e.touches?.length === 1 && scale > 1) {
        const t = e.touches[0];
        dragStartRef.current = { x: t.clientX, y: t.clientY, baseX: pos.x, baseY: pos.y };
      }
    };

    const onTouchMoveNative = (e) => {
      e.preventDefault();

      if (e.touches?.length === 2 && pinchRef.current.active) {
        const t1 = e.touches[0];
        const t2 = e.touches[1];

        const curDist = dist(t1, t2) || 1;
        const ratio = curDist / (pinchRef.current.startDist || 1);
        const nextScale = clamp(pinchRef.current.startScale * ratio, 1, 6);

        const curMid = mid(t1, t2);
        const dx = curMid.x - pinchRef.current.startMid.x;
        const dy = curMid.y - pinchRef.current.startMid.y;

        const scaleDelta = nextScale / (pinchRef.current.startScale || 1);
        const nextPos = {
          x: pinchRef.current.startPos.x * scaleDelta + dx,
          y: pinchRef.current.startPos.y * scaleDelta + dy,
        };

        setScale(nextScale);
        setPos(nextScale === 1 ? { x: 0, y: 0 } : nextPos);
        return;
      }

      if (e.touches?.length === 1 && scale > 1) {
        const t = e.touches[0];
        const s = dragStartRef.current;
        setPos({ x: s.baseX + (t.clientX - s.x), y: s.baseY + (t.clientY - s.y) });
      }
    };

    const onTouchEndNative = () => {
      pinchRef.current.active = false;
      draggingRef.current = false;
      if (scale === 1) setPos({ x: 0, y: 0 });
    };

    el.addEventListener("wheel", onWheelNative, { passive: false });
    el.addEventListener("touchstart", onTouchStartNative, { passive: false });
    el.addEventListener("touchmove", onTouchMoveNative, { passive: false });
    el.addEventListener("touchend", onTouchEndNative, { passive: false });
    el.addEventListener("touchcancel", onTouchEndNative, { passive: false });

    return () => {
      el.removeEventListener("wheel", onWheelNative);
      el.removeEventListener("touchstart", onTouchStartNative);
      el.removeEventListener("touchmove", onTouchMoveNative);
      el.removeEventListener("touchend", onTouchEndNative);
      el.removeEventListener("touchcancel", onTouchEndNative);
    };
  }, [open, scale, pos]);

  if (!open) return null;

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    draggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY, baseX: pos.x, baseY: pos.y };
  };
  const onMouseMove = (e) => {
    if (!draggingRef.current) return;
    const s = dragStartRef.current;
    setPos({ x: s.baseX + (e.clientX - s.x), y: s.baseY + (e.clientY - s.y) });
  };
  const onMouseUp = () => (draggingRef.current = false);

  const onDoubleClick = () => {
    if (scale === 1) setScale(2.2);
    else {
      setScale(1);
      setPos({ x: 0, y: 0 });
    }
  };

  const goPrev = () => {
    if (!canNav) return;
    resetView();
    onPrev?.();
  };
  const goNext = () => {
    if (!canNav) return;
    resetView();
    onNext?.();
  };

  const NavBtn = ({ disabled, onClick, children }) => (
    <button
      disabled={disabled}
      onClick={onClick}
      style={{
        border: "none",
        background: disabled ? "rgba(2,72,55,0.35)" : "#0f1c26",
        color: "#fff",
        padding: "12px 14px",
        borderRadius: 14,
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 900,
        display: "flex",
        alignItems: "center",
        gap: 8,
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </button>
  );

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 5000 }} />
      <div
        role="dialog"
        aria-modal="true"
        style={{ position: "fixed", inset: 0, zIndex: 5001, display: "grid", placeItems: "center", padding: 16 }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(1200px, 100%)",
            height: "min(88vh, 900px)",
            background: "#fff",
            borderRadius: 18,
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              background: "#f9fafb",
              borderBottom: "1px solid #e5e7eb",
              gap: 10,
            }}
          >
            <div
              style={{
                fontWeight: 900,
                color: "#0f1c26",
                fontSize: 14,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={title || "Preview"}
            >
              {title || "Preview"} — {slideLabel} ({index + 1}/{images?.length || 1}) • Zoom: {Math.round(scale * 100)}%
            </div>

            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <NavBtn disabled={!canNav} onClick={goPrev}>
                <FontAwesomeIcon icon={faChevronLeft} /> Prev
              </NavBtn>

              <NavBtn disabled={!canNav} onClick={goNext}>
                Next <FontAwesomeIcon icon={faChevronRight} />
              </NavBtn>

              <button
                onClick={onClose}
                style={{
                  border: "none",
                  background: "#111",
                  color: "#fff",
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  cursor: "pointer",
                  display: "grid",
                  placeItems: "center",
                }}
                aria-label="Close"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div
            ref={viewerRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onDoubleClick={onDoubleClick}
            style={{
              flex: 1,
              background: "#111",
              position: "relative",
              overflow: "hidden",
              cursor: scale > 1 ? (draggingRef.current ? "grabbing" : "grab") : "zoom-in",
              touchAction: "none",
              userSelect: "none",
              overscrollBehavior: "contain",
            }}
          >
            <img
              src={src}
              alt={slideLabel}
              draggable={false}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
                transformOrigin: "center",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                borderRadius: 10,
                background: "#111",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

/* =========================================================
   ✅ PlotPanel: hides pricing when status is sold/block/blocked
   ========================================================= */
const PlotPanel = ({ selectedPlotId, selectedPlotData, panelLoading, closePlotPanel, windowWidth }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const carouselRef = useRef(null);

  const [popupOpen, setPopupOpen] = useState(false);
  const [popupIndex, setPopupIndex] = useState(0);
  const [popupTitle, setPopupTitle] = useState("");

  const isMobile = windowWidth <= 768;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "unset");
  }, []);

  // ✅ Status
  const statusValue = selectedPlotData?.status ?? selectedPlotData?.Status ?? "N/A";
  const statusLower = String(statusValue || "").toLowerCase().trim();
  const canBook = statusLower === "available"; // only available can see price + book

  // ✅ Areas
  const builtUpValue = selectedPlotData?.builtUpAreaSqft ?? 0;
  const landAreaValue = selectedPlotData?.landAreaSqft ?? selectedPlotData?.LandAreaSqft ?? 0;

  // ✅ Final price
  const finalPrice = Number(selectedPlotData?.inclusivePrice ?? 0);

  const formatINR = (v) => {
    const n = Number(v);
    if (!n || Number.isNaN(n)) return "N/A";
    return `₹${n.toLocaleString("en-IN")}`;
  };

  const formatSqft = (v) => {
    const n = Number(v);
    if (!n || Number.isNaN(n)) return "N/A";
    return `${n.toLocaleString("en-IN")} sqft`;
  };

  const getStatusStyle = (status) => {
    const lower = String(status || "").toLowerCase();
    if (lower === "available") return { backgroundColor: "#e8f5e9", color: "#0f1c26" };
    if (lower === "sold") return { backgroundColor: "#ffebee", color: "#c62828" };
    if (lower === "blocked" || lower === "block") return { backgroundColor: "#fff3e0", color: "#e65100" };
    return { backgroundColor: "#eef2ff", color: "#3730a3" };
  };

  /* =========================================================
     ✅ FloorPlan mapping from data:
     floorPlan = 960 -> /floorplan/960.jpg
     floorPlan = 1115 -> /floorplan/1115.jpg
     floorPlan = 1140 -> /floorplan/1140.jpg
     floorPlan = 1160 -> /floorplan/1160.jpg
     ========================================================= */
  const floorPlanValue = selectedPlotData?.floorPlan ?? selectedPlotData?.builtUpAreaSqft ?? null;
  const floorPlanNumber = Number(floorPlanValue);
  const hasFloorPlan = Number.isFinite(floorPlanNumber) && floorPlanNumber > 0;

  const floorPlanLabel = hasFloorPlan ? `${floorPlanNumber} sqft` : "—";
  const floorPlanImage = hasFloorPlan ? `/floorplan/${floorPlanNumber}.jpg` : "";

  const villaItems = hasFloorPlan
    ? [{ label: `Floor Plan - ${floorPlanNumber} sqft`, src: floorPlanImage, type: "image" }]
    : [];

  const openPopupAt = useCallback(
    (idx) => {
      if (!villaItems.length) return;
      setPopupIndex(idx);
      setPopupTitle(`${selectedPlotData?.villaNumber || selectedPlotId} - Floor Plan`);
      setPopupOpen(true);
    },
    [villaItems.length, selectedPlotData, selectedPlotId]
  );

  const closePopup = useCallback(() => setPopupOpen(false), []);
  const popupPrev = useCallback(
    () => setPopupIndex((p) => (p - 1 + (villaItems.length || 1)) % (villaItems.length || 1)),
    [villaItems.length]
  );
  const popupNext = useCallback(() => setPopupIndex((p) => (p + 1) % (villaItems.length || 1)), [villaItems.length]);

  // Sync carousel
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || villaItems.length <= 1) return;

    const handleScroll = () => {
      const width = carousel.clientWidth || 1;
      const newIndex = Math.round(carousel.scrollLeft / width);
      setCurrentIndex(newIndex);
    };

    carousel.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => carousel.removeEventListener("scroll", handleScroll);
  }, [villaItems.length]);

  const handlePrev = () => {
    if (villaItems.length <= 1) return;
    setCurrentIndex((prev) => {
      const next = (prev - 1 + villaItems.length) % villaItems.length;
      carouselRef.current?.scrollTo({ left: next * (carouselRef.current?.clientWidth || 0), behavior: "smooth" });
      return next;
    });
  };

  const handleNext = () => {
    if (villaItems.length <= 1) return;
    setCurrentIndex((prev) => {
      const next = (prev + 1) % villaItems.length;
      carouselRef.current?.scrollTo({ left: next * (carouselRef.current?.clientWidth || 0), behavior: "smooth" });
      return next;
    });
  };

  return (
    <>
      <ZoomableGalleryPopup
        open={popupOpen}
        images={villaItems}
        index={popupIndex}
        title={popupTitle}
        onClose={closePopup}
        onPrev={popupPrev}
        onNext={popupNext}
      />

      {/* Overlay */}
      <div
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.55)", zIndex: 1999 }}
        onClick={closePlotPanel}
      />

      {/* Panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: isMobile ? "100%" : "460px",
          height: "100vh",
          backgroundColor: "#ffffff",
          boxShadow: "-12px 0 40px rgba(0,0,0,0.35)",
          zIndex: 2000,
          overflowY: "auto",
          fontFamily: "'Montserrat', sans-serif",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile close */}
        {isMobile && (
          <button
            onClick={closePlotPanel}
            style={{
              position: "fixed",
              bottom: "18px",
              right: "18px",
              zIndex: 2100,
              width: "62px",
              height: "62px",
              borderRadius: "16px",
              backgroundColor: "#0f1c26",
              color: "white",
              border: "none",
              fontSize: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 6px 20px rgba(2, 72, 55, 0.28)",
              cursor: "pointer",
            }}
            aria-label="Close panel"
          >
            ↩
          </button>
        )}

        {panelLoading ? (
          <div style={{ textAlign: "center", padding: "80px 0" }}>
            <div
              style={{
                border: "5px solid #f3f3f3",
                borderTop: "5px solid #0f1c26",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                animation: "spin 1s linear infinite",
                margin: "0 auto 16px",
              }}
            />
            <p style={{ fontSize: "15px", color: "#333" }}>Loading villa details...</p>
          </div>
        ) : selectedPlotData ? (
          <>
            {/* Top area */}
            <div
              style={{
                position: "relative",
                height: isMobile ? "280px" : "360px",
                overflow: "hidden",
                background: "#0b0f0f",
              }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {villaItems.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "12px",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(0,0,0,0.55)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "38px",
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 15,
                      opacity: isMobile || isHovered ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                    aria-label="Previous"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} size="lg" />
                  </button>

                  <button
                    onClick={handleNext}
                    style={{
                      position: "absolute",
                      top: "50%",
                      right: "12px",
                      transform: "translateY(-50%)",
                      backgroundColor: "rgba(0,0,0,0.55)",
                      color: "white",
                      border: "none",
                      borderRadius: "50%",
                      width: "38px",
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      zIndex: 15,
                      opacity: isMobile || isHovered ? 1 : 0,
                      transition: "opacity 0.3s ease",
                    }}
                    aria-label="Next"
                  >
                    <FontAwesomeIcon icon={faChevronRight} size="lg" />
                  </button>
                </>
              )}

              {hasFloorPlan ? (
                <div
                  ref={carouselRef}
                  className="pp-carousel"
                  style={{
                    display: "flex",
                    overflowX: "auto",
                    scrollSnapType: "x mandatory",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    WebkitOverflowScrolling: "touch",
                    height: "100%",
                    background: "#fff",
                  }}
                >
                  {villaItems.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => openPopupAt(idx)}
                      style={{
                        width: "100%",
                        height: "100%",
                        flexShrink: 0,
                        scrollSnapAlign: "start",
                        cursor: "pointer",
                        display: "grid",
                        placeItems: "center",
                        background: "#ffffff",
                      }}
                    >
                      <img
                        src={item.src}
                        alt={item.label}
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          background: "#fff",
                          cursor: "zoom-in",
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    padding: "20px",
                    textAlign: "center",
                    color: "#fff",
                    background: "linear-gradient(135deg, #0f1c26, #0b3b2e)",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "20px", fontWeight: 900, marginBottom: "8px" }}>Floor Plan</div>
                    <div style={{ fontSize: "13px", opacity: 0.9 }}>Floor plan is not configured.</div>
                  </div>
                </div>
              )}

              <div style={{ position: "absolute", top: "22px", left: "22px", zIndex: 10, pointerEvents: "none" }}>
                <h1 style={{ fontSize: isMobile ? "24px" : "30px", fontWeight: "900", color: "#0f1c26", margin: 0 }}>
                  Clarks exotica
                </h1>
                <p style={{ fontSize: "13px", color: "#0f1c26", margin: "8px 0 0", fontWeight: "400" }}>
                  {selectedPlotData.configuration || "Ultra Luxury Villa"} - Floor Plan
                </p>
              </div>

              {villaItems.length > 1 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "16px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    display: "flex",
                    gap: "8px",
                    zIndex: 10,
                  }}
                >
                  {villaItems.map((_, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: "7px",
                        height: "7px",
                        borderRadius: "50%",
                        backgroundColor: idx === currentIndex ? "#ffffff" : "rgba(255,255,255,0.6)",
                        transition: "all 0.3s ease",
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Body */}
            <div style={{ padding: "20px 24px 32px" }}>
              {/* Header */}
              <div
                style={{
                  marginBottom: "18px",
                  padding: "16px 18px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "16px",
                  border: "1px solid #e5e7eb",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div>
                  <h2 style={{ fontSize: isMobile ? "20px" : "24px", margin: 0, fontWeight: "800", color: "#0f1c26" }}>
                    {selectedPlotData.villaNumber || "Villa"}
                  </h2>

                  <p
                    style={{
                      margin: "6px 0 0",
                      fontSize: "14px",
                      color: "#444",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FontAwesomeIcon icon={faCompass} style={{ color: "#0f1c26" }} />
                    {selectedPlotData.facing || "N/A"} Facing
                  </p>
                </div>

                <span
                  style={{
                    ...getStatusStyle(statusValue),
                    padding: "8px 16px",
                    borderRadius: "30px",
                    fontWeight: "800",
                    fontSize: "13px",
                    letterSpacing: "0.5px",
                  }}
                >
                  {statusValue}
                </span>
              </div>

              {/* Details */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "14px",
                  marginBottom: "22px",
                }}
              >
                {[
                  { icon: faHashtag, label: "Villa ID", value: selectedPlotData.villaId || selectedPlotId },
                  { icon: faHome, label: "Configuration", value: selectedPlotData.configuration || "—" },
                  { icon: faRulerCombined, label: "Built-up Area", value: formatSqft(builtUpValue) },
                  { icon: faRulerCombined, label: "Land Area", value: formatSqft(landAreaValue) },
                  { icon: faFileAlt, label: "Floor Plan", value: floorPlanLabel || "—" },
                ].map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px 14px",
                      backgroundColor: "#f9fafb",
                      borderRadius: "12px",
                      border: "1px solid #e5e7eb",
                    }}
                  >
                    <FontAwesomeIcon icon={item.icon} style={{ color: "#0f1c26", fontSize: "18px" }} />
                    <div>
                      <div style={{ fontSize: "12px", color: "#666", fontWeight: "600" }}>{item.label}</div>
                      <div style={{ fontSize: "14px", color: "#0f1c26", fontWeight: "800", marginTop: "2px" }}>
                        {item.value || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ✅ Pricing (ONLY when Available) */}
              {canBook && (
                <div style={{ marginBottom: "22px" }}>
                  <h3 style={{ fontSize: "18px", fontWeight: "800", marginBottom: "12px", color: "#0f1c26" }}>
                    Pricing Details
                  </h3>

                  <div
                    style={{
                      backgroundColor: "#f9fafb",
                      borderRadius: "16px",
                      border: "1px solid #e5e7eb",
                      overflow: "hidden",
                    }}
                  >
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: "14px 16px", fontSize: "14px", fontWeight: "900", color: "#0f1c26" }}>
                            Final Price
                          </td>
                          <td
                            style={{
                              padding: "14px 16px",
                              fontSize: "16px",
                              fontWeight: "900",
                              color: "#0f1c26",
                              textAlign: "right",
                            }}
                          >
                            {formatINR(finalPrice)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p
                    style={{
                      fontSize: "12px",
                      color: "#777",
                      textAlign: "center",
                      marginTop: "10px",
                      fontStyle: "italic",
                    }}
                  >
                    *Final amount may vary based on booking rules.
                  </p>
                </div>
              )}

              {/* ✅ PaymentHandler (ONLY when Available) */}
              {canBook ? (
                <PaymentHandler
                  plotData={selectedPlotData}
                  plotStatus={statusValue}
                  projectId={selectedPlotId}
                  setPlotStatus={() => {}}
                  closeParentPopup={closePlotPanel}
                />
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "18px",
                    backgroundColor: "#f9f9f9",
                    borderRadius: "14px",
                    margin: "8px 0 18px",
                    border: "1px dashed #ddd",
                  }}
                >
                  <p style={{ color: "#555", fontSize: "14px", margin: 0, lineHeight: "1.5" }}>
                    This villa is currently <b>{statusValue || "not available"}</b>.
                  </p>
                </div>
              )}

              {/* Explore */}
              <div style={{ marginTop: "18px", textAlign: "center" }}>
                <button
                  onClick={closePlotPanel}
                  style={{
                    padding: "16px 18px",
                    fontSize: "15px",
                    width: "100%",
                    fontWeight: "800",
                    color: "#0f1c26",
                    backgroundColor: "transparent",
                    border: "2px solid #0f1c26",
                    borderRadius: "16px",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                    boxShadow: "0 2px 8px rgba(2,72,55,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#0f1c26";
                    e.currentTarget.style.color = "#ffffff";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "#0f1c26";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  Explore More Villas <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>

              <p
                style={{
                  textAlign: "center",
                  fontSize: "12px",
                  color: "#999",
                  marginTop: "18px",
                  fontStyle: "italic",
                  marginBottom: "90px",
                }}
              >
                Secure your dream villa today
              </p>
            </div>
          </>
        ) : (
          <p style={{ textAlign: "center", padding: "80px 0", color: "#666" }}>No data available for this villa.</p>
        )}
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .pp-carousel::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
};

export default PlotPanel;
